import { all, put, call, takeLatest } from 'redux-saga/effects'

import axios from 'portal/utils/axios-msal'

import {
  DESIGN_MATCHES_V2_LOAD,
  DesignMatchesV2LoadAction,
  designMatchesV2LoadFailure,
  designMatchesV2LoadSuccess
} from './actions'
import {
  DesignMatchesV2Response,
  VerificationCheck,
  VerificationCheckData,
  VerificationCheckEntry,
  VerificationCheckSource
} from './types'

const toValue = (value: unknown) => `${value ?? ''}`

const toSource = (entry: Partial<VerificationCheckEntry>) => {
  const sourceNames = Array.isArray(entry?.sources)
    ? entry.sources
        .map(source => toValue(source?.documentName).trim())
        .filter(Boolean)
    : []

  if (sourceNames.length > 0) {
    return sourceNames.join(', ')
  }

  return toValue(entry?.note)
}

const normalizeSources = (
  entry: Partial<VerificationCheckEntry>
): VerificationCheckSource[] => {
  if (!Array.isArray(entry?.sources)) {
    return []
  }

  return entry.sources.map(source => ({
    documentName: toValue(source?.documentName),
    documentLink: source?.documentLink || null,
    documentPageNumber: Number(source?.documentPageNumber || 0),
    extractedContent: toValue(source?.extractedContent)
  }))
}

const normalizeEntry = (
  productId: string,
  entry: Partial<VerificationCheckEntry>,
  index: number
): VerificationCheck => ({
  id: `${productId}-${index}`,
  condition: toValue(entry?.condition),
  required: toValue(entry?.required),
  delivered: toValue(entry?.delivered),
  status: toValue(entry?.status),
  source: toSource(entry),
  sources: normalizeSources(entry),
  note: toValue(entry?.note)
})

function normalizeData(
  rawData: Partial<DesignMatchesV2Response>
): VerificationCheckData {
  const productResults = rawData?.productResults || {}

  return Object.entries(productResults).reduce(
    (acc: VerificationCheckData, [productResultKey, productResult]) => {
      const productIdFromKey = toValue(productResultKey).trim()
      const productIdFromPayload = toValue(productResult?.productId).trim()
      const productId = productIdFromKey || productIdFromPayload
      if (!productId) {
        return acc
      }

      const entries = Array.isArray(productResult?.entries)
        ? productResult.entries
        : []

      acc[productId] = {
        conditionsMet: productResult?.conditionsMet || 0,
        totalConditions: entries.length,
        checks: entries.map((entry, index) =>
          normalizeEntry(productId, entry, index)
        )
      }

      return acc
    },
    {}
  )
}

function* designMatchesV2LoadSaga(action: DesignMatchesV2LoadAction) {
  const { accountId, projectId, recordId } = action

  try {
    const response: { data: DesignMatchesV2Response } = yield call(() =>
      axios.get(
        `/accounts/${accountId}/projects/${projectId}/modules/delivery/records/${recordId}/designmatchesv2`
      )
    )

    yield put(designMatchesV2LoadSuccess(normalizeData(response?.data)))
  } catch (error) {
    yield put(designMatchesV2LoadFailure(error as Error))
  }
}

export function* watch() {
  yield all([takeLatest(DESIGN_MATCHES_V2_LOAD, designMatchesV2LoadSaga)])
}
